/**
 * onboarding-contact-shape — shape-tolerant string extraction for the onboarding
 * contacts beat (audit finding F14).
 *
 * `pick(o, key)` in onboarding-beats-shared reaches into raw `attributes`, which
 * — post canonical-schema — can hold a bare string, a `{ value, … }` object, or
 * an array of either (emails/phones are now `{ value, … }[]`). Coercing those
 * with `String(...)` yields `"[object Object]"` on the object/array shapes, so
 * the contacts beat could render garbled values and its dedup key (name|title|
 * email) would stop collapsing object-shaped duplicates.
 *
 * `resolveStr` normalizes any of those shapes to the underlying display string
 * (taking the first non-empty `.value` for arrays). It never returns
 * `"[object Object]"`: an object without a `value` resolves to `''`.
 */
export function resolveStr(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = resolveStr(item);
      if (resolved) return resolved;
    }
    return '';
  }
  if (typeof value === 'object') {
    // Canonical per-value shape: { value, … }. Anything else (no `value`)
    // resolves to '' rather than coercing the object to "[object Object]".
    return resolveStr((value as { value?: unknown }).value);
  }
  return '';
}
